import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import {
  MdCreditCard, MdUpload, MdDelete, MdDownload, MdImage,
  MdAdd, MdClose, MdFilterList, MdPhoto
} from 'react-icons/md';
import { AuthContext } from '../context/AuthContext';
import './BusinessCard.css';

// Predefined sector options (admin can also type a custom one)
const SECTOR_OPTIONS = [
  'Hotel',
  'School',
  'Hospital',
  'Restaurant',
  'Retail',
  'Real Estate',
  'Technology',
  'Finance',
  'Construction',
  'Transportation',
  'Other'
];

const BusinessCard = () => {
  const { user } = useContext(AuthContext);

  // --- Business Card state ---
  const [businessCard, setBusinessCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // --- Posters state ---
  const [posters, setPosters] = useState([]);
  const [postersLoading, setPostersLoading] = useState(true);
  const [activeSector, setActiveSector] = useState('All');
  const [allSectors, setAllSectors] = useState([]);
  const [posterMessage, setPosterMessage] = useState({ type: '', text: '' });

  // Upload poster modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [posterForm, setPosterForm] = useState({ sector: '', customSector: '', title: '' });
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [uploadingPoster, setUploadingPoster] = useState(false);

  // Lightbox state
  const [lightbox, setLightbox] = useState(null);

  const posterFileRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchBusinessCard();
    fetchPosters();
  }, []);

  // ─── Business Card ────────────────────────────────────────────────────────

  const fetchBusinessCard = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/business-card');
      setBusinessCard(response.data.business_card_url || null);
    } catch (error) {
      console.error('Error fetching business card:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select a valid image file.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 5MB.' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const response = await axios.post('/api/business-card/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBusinessCard(response.data.business_card_url);
      setMessage({ type: 'success', text: 'Business card uploaded successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error uploading business card:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to upload business card' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the business card? This will affect all users.')) return;
    try {
      await axios.delete('/api/business-card');
      setBusinessCard(null);
      setMessage({ type: 'success', text: 'Business card deleted successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting business card:', error);
      setMessage({ type: 'error', text: 'Failed to delete business card' });
    }
  };

  const handleDownload = async () => {
    if (!businessCard) return;
    setDownloading(true);
    try {
      const response = await fetch(businessCard);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'jiruutech-business-card.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      setMessage({ type: 'error', text: 'Download failed. Try right-clicking the image and saving it.' });
    } finally {
      setDownloading(false);
    }
  };

  // ─── Posters ─────────────────────────────────────────────────────────────

  const fetchPosters = async () => {
    try {
      setPostersLoading(true);
      const [postersRes, sectorsRes] = await Promise.all([
        axios.get('/api/posters'),
        axios.get('/api/posters/sectors')
      ]);
      setPosters(postersRes.data.posters || []);
      setAllSectors(sectorsRes.data.sectors || []);
    } catch (error) {
      console.error('Error fetching posters:', error);
    } finally {
      setPostersLoading(false);
    }
  };

  const filteredPosters =
    activeSector === 'All'
      ? posters
      : posters.filter(p => p.sector === activeSector);

  const handlePosterFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setPosterMessage({ type: 'error', text: 'Please select a valid image file.' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPosterMessage({ type: 'error', text: 'Image size must be less than 10MB.' });
      return;
    }
    setPosterFile(file);
    setPosterPreview(URL.createObjectURL(file));
  };

  const handlePosterUpload = async (e) => {
    e.preventDefault();
    if (!posterFile) {
      setPosterMessage({ type: 'error', text: 'Please select an image.' });
      return;
    }

    const sector =
      posterForm.sector === 'Other' || posterForm.sector === ''
        ? posterForm.customSector.trim()
        : posterForm.sector;

    if (!sector) {
      setPosterMessage({ type: 'error', text: 'Please select or enter a sector.' });
      return;
    }
    if (!posterForm.title.trim()) {
      setPosterMessage({ type: 'error', text: 'Please enter a title.' });
      return;
    }

    setUploadingPoster(true);
    setPosterMessage({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('photo', posterFile);
      formData.append('sector', sector);
      formData.append('title', posterForm.title.trim());

      await axios.post('/api/posters/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPosterMessage({ type: 'success', text: 'Poster uploaded successfully!' });
      setTimeout(() => setPosterMessage({ type: '', text: '' }), 3000);
      setShowUploadModal(false);
      resetPosterForm();
      fetchPosters();
    } catch (error) {
      console.error('Error uploading poster:', error);
      setPosterMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to upload poster'
      });
    } finally {
      setUploadingPoster(false);
    }
  };

  const handleDeletePoster = async (id) => {
    if (!window.confirm('Delete this poster? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/posters/${id}`);
      setPosterMessage({ type: 'success', text: 'Poster deleted successfully!' });
      setTimeout(() => setPosterMessage({ type: '', text: '' }), 3000);
      setPosters(prev => prev.filter(p => p.id !== id));
      // Refresh sectors list
      const sectorsRes = await axios.get('/api/posters/sectors');
      setAllSectors(sectorsRes.data.sectors || []);
      if (!sectorsRes.data.sectors.includes(activeSector)) setActiveSector('All');
    } catch (error) {
      console.error('Error deleting poster:', error);
      setPosterMessage({ type: 'error', text: 'Failed to delete poster' });
    }
  };

  const handleDownloadPoster = async (poster) => {
    try {
      const response = await fetch(poster.image_url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${poster.title.replace(/\s+/g, '-').toLowerCase()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      setPosterMessage({ type: 'error', text: 'Download failed.' });
    }
  };

  const resetPosterForm = () => {
    setPosterForm({ sector: '', customSector: '', title: '' });
    setPosterFile(null);
    setPosterPreview(null);
    if (posterFileRef.current) posterFileRef.current.value = '';
  };

  const closeModal = () => {
    setShowUploadModal(false);
    resetPosterForm();
    setPosterMessage({ type: '', text: '' });
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="app-container">
        <Sidebar />
        <div className="main-content business-card-main">
          <div className="business-card-page">
            <div className="page-header">
              <h1><MdCreditCard /> Business Card</h1>
            </div>
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-content business-card-main">
        <div className="business-card-page">

          {/* ── Page Header ── */}
          <div className="page-header">
            <div className="header-content">
              <h1><MdCreditCard /> Business Card</h1>
              <p className="header-subtitle">
                {isAdmin
                  ? 'Upload and manage the company business card and sector posters'
                  : 'View and download the company business card and sector posters'}
              </p>
            </div>
          </div>

          {/* ── Business Card Message ── */}
          {message.text && (
            <div className={`message-banner ${message.type}`}>
              {message.text}
            </div>
          )}

          {/* ── Business Card Section ── */}
          <div className="business-card-container">
            {businessCard ? (
              <div className="business-card-display">
                <div className="card-preview">
                  <img src={businessCard} alt="Business Card" className="card-image" />
                </div>

                <div className="card-actions">
                  <button
                    className="btn-action btn-download"
                    onClick={handleDownload}
                    disabled={downloading}
                  >
                    <MdDownload /> {downloading ? 'Downloading...' : 'Download Card'}
                  </button>

                  {isAdmin && (
                    <>
                      <label className={`btn-action btn-replace ${uploading ? 'disabled' : ''}`}>
                        <MdUpload />
                        {uploading ? 'Uploading...' : 'Replace Card'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleUpload}
                          style={{ display: 'none' }}
                          disabled={uploading}
                        />
                      </label>

                      <button
                        className="btn-action btn-delete"
                        onClick={handleDelete}
                      >
                        <MdDelete /> Delete Card
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="business-card-empty">
                <div className="empty-icon">
                  <MdCreditCard />
                </div>
                <h2>No Business Card Available</h2>
                {isAdmin ? (
                  <>
                    <p>Upload a business card that will be visible to all users</p>
                    <label className="btn-upload-primary">
                      <MdUpload />
                      {uploading ? 'Uploading...' : 'Upload Business Card'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleUpload}
                        style={{ display: 'none' }}
                        disabled={uploading}
                      />
                    </label>
                    <p className="upload-hint">
                      <MdImage /> Supported formats: JPG, PNG, GIF (Max 5MB)
                    </p>
                  </>
                ) : (
                  <p>The admin hasn't uploaded a business card yet</p>
                )}
              </div>
            )}
          </div>

          {/* ══════════════════════════════════════════════════════════════════
              SECTOR POSTERS SECTION
          ══════════════════════════════════════════════════════════════════ */}
          <div className="posters-section">
            <div className="posters-header">
              <div className="posters-header-left">
                <h2><MdPhoto /> Sector Posters</h2>
                <p className="posters-subtitle">
                  {isAdmin
                    ? 'Upload and manage promotional posters by sector'
                    : 'Browse and download promotional posters by sector'}
                </p>
              </div>
              {isAdmin && (
                <button
                  className="btn-add-poster"
                  onClick={() => setShowUploadModal(true)}
                >
                  <MdAdd /> Add Poster
                </button>
              )}
            </div>

            {/* Poster message banner */}
            {posterMessage.text && (
              <div className={`message-banner ${posterMessage.type}`}>
                {posterMessage.text}
              </div>
            )}

            {/* Sector filter tabs */}
            {allSectors.length > 0 && (
              <div className="sector-tabs">
                <MdFilterList className="filter-icon" />
                <button
                  className={`sector-tab ${activeSector === 'All' ? 'active' : ''}`}
                  onClick={() => setActiveSector('All')}
                >
                  All ({posters.length})
                </button>
                {allSectors.map(sector => (
                  <button
                    key={sector}
                    className={`sector-tab ${activeSector === sector ? 'active' : ''}`}
                    onClick={() => setActiveSector(sector)}
                  >
                    {sector} ({posters.filter(p => p.sector === sector).length})
                  </button>
                ))}
              </div>
            )}

            {/* Posters grid */}
            {postersLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading posters...</p>
              </div>
            ) : filteredPosters.length === 0 ? (
              <div className="posters-empty">
                <div className="empty-icon"><MdPhoto /></div>
                <h3>No Posters Yet</h3>
                {isAdmin ? (
                  <p>Click "Add Poster" to upload your first sector poster</p>
                ) : (
                  <p>No posters have been uploaded yet</p>
                )}
              </div>
            ) : (
              <div className="posters-grid">
                {filteredPosters.map(poster => (
                  <div key={poster.id} className="poster-card">
                    <div
                      className="poster-image-wrap"
                      onClick={() => setLightbox(poster)}
                      title="Click to enlarge"
                    >
                      <img src={poster.image_url} alt={poster.title} className="poster-image" />
                      <div className="poster-overlay">
                        <MdImage className="overlay-icon" />
                      </div>
                    </div>
                    <div className="poster-info">
                      <span className="poster-sector-badge">{poster.sector}</span>
                      <h4 className="poster-title">{poster.title}</h4>
                      <div className="poster-actions">
                        <button
                          className="btn-poster-action btn-poster-download"
                          onClick={() => handleDownloadPoster(poster)}
                          title="Download"
                        >
                          <MdDownload /> Download
                        </button>
                        {isAdmin && (
                          <button
                            className="btn-poster-action btn-poster-delete"
                            onClick={() => handleDeletePoster(poster.id)}
                            title="Delete"
                          >
                            <MdDelete />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Upload Poster Modal ── */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><MdUpload /> Upload Sector Poster</h3>
              <button className="modal-close" onClick={closeModal}><MdClose /></button>
            </div>

            {posterMessage.text && (
              <div className={`message-banner ${posterMessage.type}`} style={{ margin: '0 0 16px' }}>
                {posterMessage.text}
              </div>
            )}

            <form onSubmit={handlePosterUpload} className="poster-form">
              {/* Sector select */}
              <div className="form-group">
                <label>Sector *</label>
                <select
                  value={posterForm.sector}
                  onChange={e => setPosterForm(prev => ({ ...prev, sector: e.target.value, customSector: '' }))}
                  required={posterForm.sector !== 'Other' && posterForm.sector !== ''}
                >
                  <option value="">-- Select a sector --</option>
                  {SECTOR_OPTIONS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Custom sector input (shown when "Other" or empty) */}
              {(posterForm.sector === 'Other' || posterForm.sector === '') && (
                <div className="form-group">
                  <label>Custom Sector Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. Agriculture, Mining..."
                    value={posterForm.customSector}
                    onChange={e => setPosterForm(prev => ({ ...prev, customSector: e.target.value }))}
                  />
                </div>
              )}

              {/* Title */}
              <div className="form-group">
                <label>Poster Title *</label>
                <input
                  type="text"
                  placeholder="e.g. Grand Hotel Opening"
                  value={posterForm.title}
                  onChange={e => setPosterForm(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>

              {/* File picker */}
              <div className="form-group">
                <label>Image *</label>
                <div
                  className="file-drop-zone"
                  onClick={() => posterFileRef.current?.click()}
                >
                  {posterPreview ? (
                    <img src={posterPreview} alt="Preview" className="file-preview-img" />
                  ) : (
                    <>
                      <MdImage className="drop-icon" />
                      <p>Click to select an image</p>
                      <span>JPG, PNG, GIF, WebP — max 10MB</span>
                    </>
                  )}
                </div>
                <input
                  ref={posterFileRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePosterFileChange}
                  style={{ display: 'none' }}
                />
                {posterFile && (
                  <p className="file-name-hint">
                    Selected: {posterFile.name}
                    <button
                      type="button"
                      className="clear-file-btn"
                      onClick={() => { setPosterFile(null); setPosterPreview(null); if (posterFileRef.current) posterFileRef.current.value = ''; }}
                    >
                      <MdClose />
                    </button>
                  </p>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-modal-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-modal-submit" disabled={uploadingPoster}>
                  <MdUpload /> {uploadingPoster ? 'Uploading...' : 'Upload Poster'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Lightbox ── */}
      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <div className="lightbox-box" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightbox(null)}><MdClose /></button>
            <img src={lightbox.image_url} alt={lightbox.title} className="lightbox-image" />
            <div className="lightbox-caption">
              <span className="poster-sector-badge">{lightbox.sector}</span>
              <span className="lightbox-title">{lightbox.title}</span>
              <button
                className="btn-poster-action btn-poster-download"
                onClick={() => handleDownloadPoster(lightbox)}
              >
                <MdDownload /> Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessCard;
